'use client';

import { Mail, Phone, MapPin, ShieldCheck, SendHorizonal } from 'lucide-react';
import type { SiteContent } from '@/lib/content';
import { trackFormSubmit } from '@/components/trackable-link';
import { useEffect, useRef, useState } from 'react';

const iconMap: Record<string, typeof Mail> = {
  'E-mail': Mail,
  Email: Mail,
  Téléphone: Phone,
  Telefon: Phone,
  Phone: Phone,
  Telefoon: Phone,
  Adres: MapPin,
  Adresse: MapPin,
  Office: MapPin,
  'Büro': MapPin,
  Bureau: MapPin,
};

interface ContactSectionProps {
  content: SiteContent['contact'];
}

// Declare Turnstile type
declare global {
  interface Window {
    turnstile?: {
      render: (element: string | HTMLElement, options: {
        sitekey: string;
        callback?: (token: string) => void;
        'error-callback'?: () => void;
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'compact';
      }) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export function ContactSection({ content }: ContactSectionProps) {
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [shouldLoadTurnstile, setShouldLoadTurnstile] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string>('');

  // Load Cloudflare Turnstile script after it is triggered
  useEffect(() => {
    if (!shouldLoadTurnstile) {
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.turnstile && turnstileRef.current) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '1x00000000000000000000AA', // Test key
          callback: (token: string) => {
            setTurnstileToken(token);
          },
          'error-callback': () => {
            setTurnstileToken('');
          },
          theme: 'light',
          // The normal widget is a fixed 300px wide. Inside the padded card
          // that is wider than a 375px screen can hold, and it dragged the
          // whole page into horizontal scroll. Compact is 150px.
          size: window.innerWidth < 420 ? 'compact' : 'normal',
        });
      }
    };

    return () => {
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.remove(widgetIdRef.current);
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [shouldLoadTurnstile]);
  
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    
    if (!turnstileToken) {
      alert('Controleer de captcha om door te gaan.');
      return;
    }
    
    setIsSubmitting(true);
    const form = e.currentTarget;
    const formData = new FormData(form);
    const name = formData.get('name');
    const email = formData.get('email');
    const message = formData.get('message');
    trackFormSubmit('Contact Form', true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, message, turnstileToken })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        alert('Bedankt! Je bericht is ontvangen.');
        form.reset();
        // Reset turnstile
        if (window.turnstile && widgetIdRef.current) {
          window.turnstile.reset(widgetIdRef.current);
        }
        setTurnstileToken('');
      } else {
        alert(data.error || 'Er is iets misgegaan. Probeer het later opnieuw.');
      }
    } catch {
      alert('Er is een netwerkfout opgetreden.');
    } finally {
      setIsSubmitting(false);
    }
  }
  return (
    <section id="contact" className="section-padding bg-ledger" aria-labelledby="contact-heading">
      <div className="container-wide grid gap-10 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1fr)]">
        <div className="min-w-0 space-y-6">
          <h2 id="contact-heading" className="text-balance text-3xl font-bold tracking-[-0.03em] text-ink sm:text-5xl">{content.title}</h2>
          <p className="text-lg text-slate-700">{content.description}</p>
          <ul className="space-y-4 text-sm text-slate-700">
            {content.details.map((detail) => {
              const Icon = iconMap[detail.label] ?? ShieldCheck;
              return (
                // items-start + min-w-0: the office address is long enough to
                // push the row past a phone screen if it is not allowed to wrap.
                <li key={detail.label} className="flex items-start gap-3">
                  <span
                    className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-ledger text-slate-500 ring-1 ring-rule"
                    aria-hidden="true"
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  {detail.href ? (
                    <a
                      href={detail.href}
                      className="min-w-0 break-words pt-2 text-sm font-semibold text-ink hover:text-orange-dark"
                      aria-label={`${detail.label}: ${detail.value}`}
                    >
                      {detail.value}
                    </a>
                  ) : (
                    // System font: the Turkish address would otherwise pull in Manrope's latin-ext file.
                    <span className="min-w-0 break-words pt-2 font-[system-ui,sans-serif] text-sm font-semibold text-slate-700">{detail.value}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
        <div className="min-w-0 rounded-2xl border border-rule bg-white p-6 sm:p-8">
          <h3 className="text-xl font-semibold text-slate-900">{content.form.title}</h3>
          <p className="mt-2 text-sm text-slate-700">{content.form.description}</p>
          <form 
            className="mt-6 space-y-4" 
            name="contact" 
            aria-label="Contact formulier"
            toolname="send_contact_message"
            tooldescription="Fill in a message to the Winkel Factuur team (sales and support). The visitor completes the security check and presses send."
            onSubmit={handleSubmit}
            // Turnstile loads on the first interaction with the form, not on scroll:
            // its iframe blocks the back/forward cache, and Lighthouse's full-page
            // screenshot put every section "in view".
            onFocus={() => setShouldLoadTurnstile(true)}
            onPointerDown={() => setShouldLoadTurnstile(true)}
          >
            <input type="hidden" name="form-name" value="contact" />
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-600" htmlFor="name">
                {content.form.nameLabel}
              </label>
              <input
                id="name"
                name="name"
                type="text"
                autoComplete="name"
                toolparamdescription="Full name of the person sending the message."
                required
                aria-required="true"
                className="mt-1.5 w-full rounded-lg border border-rule bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-orange focus:ring-2 focus:ring-orange/20"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-600" htmlFor="email">
                {content.form.emailLabel}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                toolparamdescription="E-mail address the team should reply to."
                required
                aria-required="true"
                className="mt-1.5 w-full rounded-lg border border-rule bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-orange focus:ring-2 focus:ring-orange/20"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-slate-600" htmlFor="message">
                {content.form.messageLabel}
              </label>
              <textarea
                id="message"
                name="message"
                rows={4}
                toolparamdescription="The question or request for the Winkel Factuur team."
                className="mt-1.5 w-full rounded-lg border border-rule bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-orange focus:ring-2 focus:ring-orange/20"
              />
            </div>
            
            {/* Cloudflare Turnstile Captcha */}
            <div className="flex justify-center">
              <div ref={turnstileRef} id="turnstile-widget"></div>
            </div>
            
            <button
              type="submit"
              disabled={!turnstileToken || isSubmitting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-orange px-6 py-3.5 text-sm font-bold text-ink transition-colors duration-200 hover:bg-orange-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={content.form.submitLabel}
            >
              {isSubmitting ? 'Verzenden...' : content.form.submitLabel}
              <SendHorizonal className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {content.highlights.map((highlight) => (
              <div key={highlight.title} className="rounded-lg border border-rule bg-ledger p-4">
                <h4 className="text-sm font-semibold text-slate-900">{highlight.title}</h4>
                <p className="mt-2 text-xs text-slate-700">{highlight.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
